import React, { useEffect, useState, useCallback } from 'react';
import { Link2, RefreshCw, Save } from 'lucide-react';
import apiClient from '../services/api';

const API_URL = '/control_head_assign.php';

const FIELD_GROUPS = [
  { title: 'Party Control Heads', fields: [
    { label: 'Customer Control',        key: 'customercontrolid' },
    { label: 'Supplier Control',         key: 'suppliercontrolid' },
    { label: 'Forwarding Agent Control', key: 'fagentcontrolid' },
  ]},
  { title: 'Cash & Bank', fields: [
    { label: 'Cash Control', key: 'cashcontrolid' },
    { label: 'Bank Control', key: 'bankcontrolid' },
  ]},
  { title: 'Vehicle Heads', fields: [
    { label: 'Hired Vehicles Control',       key: 'hiredvehiclescontrolid' },
    { label: 'Own Vehicles Control',         key: 'ownvehiclescontrolid' },
    { label: 'Vehicle Hire Charges Control', key: 'vehiclehirechargescontrolid' },
  ]},
  { title: 'Operational Expense Heads', fields: [
    { label: 'FOC Control',             key: 'foccontrolid' },
    { label: 'F/Agent Expense Control', key: 'fagentexpensecontrolid' },
    { label: 'Freight Charges Expense', key: 'freightchargesexpenseid' },
    { label: 'Customs Duty Expense',    key: 'customsdutyexpenseid' },
    { label: 'Other Charges Expense',   key: 'otherchargesexpenseid' },
    { label: 'Driver Charges Expense',  key: 'driverchargesexpenseid' },
  ]},
  { title: 'Special Transaction Heads', fields: [
    { label: 'TD Advance Control',        key: 'tdadvancecontrolid' },
    { label: 'COD Control',               key: 'codcontrolid' },
    { label: 'MC Receipt Control',        key: 'mcreceiptcontrolid' },
    { label: 'MC Payment Control',        key: 'mcpaymentcontrolid' },
    { label: 'Unbilled Clearing Control', key: 'unbilledclearingcontrolid' },
  ]},
  { title: 'Sales Income Heads', fields: [
    { label: 'Sales Freight Control',       key: 'salesfreightcontrolid' },
    { label: 'Sales Other Charges Control', key: 'salesotherchargescontrolid' },
    { label: 'Sales Documentation Control', key: 'salesdocumentationcontrolid' },
    { label: 'Sales Customs Duty Control',  key: 'salescustomsdutycontrolid' },
  ]},
  { title: 'Tax & Container Heads', fields: [
    { label: 'Tax Control',               key: 'taxcontrolid' },
    { label: 'Container Expense Control', key: 'containerexpensecontrolid' },
    { label: 'Container Income Control',  key: 'containerincomecontrolid' },
  ]},
  { title: 'Tax Component Control Heads', isTax: true, fields: [
    { label: 'Tax 1 Control (e.g. CGST)', key: 'tax1controlid' },
    { label: 'Tax 2 Control (e.g. SGST)', key: 'tax2controlid' },
    { label: 'Tax 3 Control (e.g. IGST)', key: 'tax3controlid' },
    { label: 'Tax 4 Control',             key: 'tax4controlid' },
    { label: 'Tax 5 Control (Fixed Amt)', key: 'tax5controlid' },
  ]},
];

const ALL_KEYS = FIELD_GROUPS.flatMap(g => g.fields.map(f => f.key));
const emptyForm = () => Object.fromEntries(ALL_KEYS.map(k => [k, '']));

const NAVY      = '#667eea';
const NAVY_DARK = '#764ba2';

export default function ControlHeadAssignForm() {
  const [heads,    setHeads]    = useState([]);
  const [formData, setFormData] = useState(emptyForm());
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [message,  setMessage]  = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true); setMessage(null);
    try {
      const res = await apiClient.get(API_URL);
      if (res.data.success) {
        setHeads(res.data.heads);
        const mapped = {};
        ALL_KEYS.forEach(k => { mapped[k] = res.data.assignment[k] ?? ''; });
        setFormData(mapped);
      } else { setMessage({ type: 'error', text: res.data.message || 'Failed to load.' }); }
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.message || err.message }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true); setMessage(null);
    try {
      const payload = {};
      ALL_KEYS.forEach(k => { payload[k] = formData[k] === '' ? null : parseInt(formData[k], 10); });
      const res = await apiClient.post(API_URL, payload);
      if (res.data.success) setMessage({ type: 'success', text: res.data.message });
      else setMessage({ type: 'error', text: res.data.message || 'Save failed.' });
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.message || err.message }); }
    finally { setSaving(false); }
  };

  const set = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  return (
    <div className="min-h-screen bg-[#f7f8fc] font-[system-ui,sans-serif] text-gray-800">
      <style>{`@keyframes spin { to { transform:rotate(360deg); } } .spinning { animation:spin 1s linear infinite; }`}</style>

      {/* Header */}
      <div className="flex items-center justify-between px-8 py-6"
        style={{ background:`linear-gradient(135deg,${NAVY} 0%,${NAVY_DARK} 100%)`, boxShadow:'0 4px 16px rgba(102,126,234,0.3)' }}>
        <div className="flex items-center gap-4">
          <div className="w-[52px] h-[52px] rounded-[13px] flex items-center justify-center flex-shrink-0 bg-white/20 shadow-lg text-white">
            <Link2 size={26} />
          </div>
          <div>
            <h2 className="m-0 text-[1.6rem] font-bold" style={{ color:'#fff', textShadow:'0 2px 4px rgba(0,0,0,0.2)' }}>
              Control Head Assignment
            </h2>
            <p className="m-0 mt-0.5 text-[0.82rem]" style={{ color:'rgba(255,255,255,0.75)' }}>
              Map account heads to system control roles
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSave} disabled={saving || loading}
            className="flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold bg-white rounded-lg border-0 cursor-pointer transition-all duration-150 hover:bg-violet-100 disabled:opacity-45 disabled:cursor-not-allowed whitespace-nowrap"
            style={{ color:'#5a3e9e' }}>
            {saving
              ? <><RefreshCw size={13} className="spinning" /> Saving…</>
              : <><Save size={14} /> Save</>}
          </button>
          <button onClick={loadData} disabled={loading || saving} title="Refresh"
            className="w-9 h-9 flex items-center justify-center rounded-lg border cursor-pointer transition-all duration-150 bg-white/15 text-white border-white/35 hover:bg-white/28 disabled:opacity-45 disabled:cursor-not-allowed">
            <RefreshCw size={15} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-8 py-6">

        {message && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-[13px] font-medium mb-5 border ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border-green-300'
              : 'bg-red-50 text-red-700 border-red-300'
          }`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-2.5 py-16 text-gray-500 text-sm">
            <RefreshCw size={18} className="spinning" /> Loading assignments…
          </div>
        ) : (
          FIELD_GROUPS.map(group => {
            const isTax = !!group.isTax;
            const headerBg = isTax
              ? 'linear-gradient(135deg,#f59e0b 0%,#d97706 100%)'
              : `linear-gradient(135deg,${NAVY} 0%,${NAVY_DARK} 100%)`;
            return (
              <div key={group.title} className="bg-white border border-gray-200 rounded-xl mb-4 overflow-hidden shadow-sm">
                {/* Section header */}
                <div className="px-4 py-2 text-[11.5px] font-bold uppercase tracking-[0.7px] text-white"
                  style={{ background: headerBg }}>
                  {group.title}
                </div>
                {/* Fields grid */}
                <div className="grid grid-cols-3 max-[900px]:grid-cols-2 max-[560px]:grid-cols-1">
                  {group.fields.map((field, fi) => (
                    <div key={field.key}
                      className="flex items-center gap-2.5 px-4 py-2.5 min-h-[50px] border-b border-r border-gray-100 last:border-r-0">
                      <span className={`text-[12px] font-semibold whitespace-nowrap min-w-[140px] flex-shrink-0 ${isTax ? 'text-amber-800' : 'text-violet-800'}`}>
                        {field.label}
                      </span>
                      <select
                        value={formData[field.key] ?? ''}
                        onChange={e => set(field.key, e.target.value)}
                        className={`flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1.5 text-[13px] text-gray-800 bg-white outline-none transition-all duration-150 cursor-pointer ${
                          isTax
                            ? 'focus:border-amber-400 focus:shadow-[0_0_0_3px_rgba(245,158,11,0.15)]'
                            : 'focus:border-violet-400 focus:shadow-[0_0_0_3px_rgba(102,126,234,0.15)]'
                        }`}>
                        <option value="">-- Select --</option>
                        {heads.map(h => (
                          <option key={h.acheadid} value={h.acheadid}>{h.acheadname}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}